import React, { Dispatch, SetStateAction } from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media, Text } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { TRACKING_EVENT_ACTION_SUBJECT_IDS } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/views/user-seg-bxp-ui/constants.ts";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { JiraTemplateChoice } from "../../../hooks";
import { UserSegmentationTeamTypes } from "../../../models/user-segmentation";
import { TemplateOptionMetadata } from "../template-definitions";

interface CardProps {
    imageSrc: string;
    mobileImgSrc: string;
    title: string;
    subtext: string;
    templateType: JiraTemplateChoice;
    templateId: string;
    setSelectedTemplate: Dispatch<SetStateAction<JiraTemplateChoice>>;
    setSelectedTemplateId: Dispatch<SetStateAction<string | undefined>>;
    selectedCardTitle: string | undefined;
    setSelectedCardTitle: Dispatch<SetStateAction<string | undefined>>;
    teamType: UserSegmentationTeamTypes;
    templateMetadata?: TemplateOptionMetadata;
    setSelectedTemplateMetadata: Dispatch<
        SetStateAction<TemplateOptionMetadata | undefined>
    >;
}

const cardBorderStyle = css({
    margin: "1px",
    padding: "0px",
    borderRadius: "var(--rounding, 8px)",
    // eslint-disable-next-line local/ensure-wac-token-usage
    border: "1px solid #C1C7D0",
    background: wacToken("color.neutral.light0"),
    overflow: "hidden",
    display: "flex",
    flexDirection: "row",
    maxWidth: "463px",
    alignItems: "center",
    cursor: "pointer",
    ["&:hover"]: {
        boxShadow:
            // eslint-disable-next-line local/ensure-wac-token-usage
            "0px 1px 1px 0px rgba(9, 30, 66, 0.25), 0px 0px 1px 0px rgba(9, 30, 66, 0.31)"
    },
    [media.above.md]: {
        flexDirection: "column",
        justifyContent: "flex-start",
        maxWidth: "none",
        minHeight: "333px",
        flex: 1
    }
});

const unselectedButtonStyle = css({
    borderColor: token("color.background.accent.gray.subtlest")
});

const selectedButtonStyle = css({
    border: `2px solid ${token("color.link")}`,
    margin: 0
});

const cardTitleStyle = css({
    fontFamily: wacToken("font.charlie.text"),
    color: token("color.text"),
    fontSize: "14px",
    fontWeight: 700,
    lineHeight: "20px" /* 125% */,
    margin: "0 0 5px 0",
    [media.above.md]: {
        fontSize: "20px",
        lineHeight: "28px"
    }
});

const cardTitleVisualUpdateStyle = css({
    fontFamily: token("font.family.heading"),
    fontWeight: token("font.weight.bold"),
    // eslint-disable-next-line local/ensure-wac-token-usage
    fontSize: "1.25rem",
    // eslint-disable-next-line local/ensure-wac-token-usage
    lineHeight: "1.75rem",
    color: token("color.text"),
    margin: 0
});

const cardDescriptionVisualUpdateStyle = css({
    fontFamily: token("font.family.body"),
    fontWeight: token("font.weight.regular"),
    // eslint-disable-next-line local/ensure-wac-token-usage
    fontSize: "1rem",
    // eslint-disable-next-line local/ensure-wac-token-usage
    lineHeight: "1.5rem",
    margin: 0
});

const cardDescriptionColorOverride = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& p": {
        color: wacToken("color.primary.black")
    }
});

const titleContainerStyle = css({
    display: "flex",
    alignItems: "center",
    marginBlockEnd: token("space.075")
});

const textContainerVisualUpdateStyle = css({
    "padding": token("space.300"),
    "gap": token("space.050"),
    "width": "100%",
    "alignItems": "flex-start",
    [media.above.md]: {
        padding: token("space.300")
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& h3, & p": {
        margin: 0,
        width: "100%"
    }
});

const titleContainerVisualUpdateStyle = css({
    marginBlockEnd: 0
});

const cardBorderVisualUpdateStyle = css({
    borderRadius: "8px",
    border: `1px solid ${token("color.background.accent.gray.subtlest")}`,
    margin: 0
});

const cardBorderSelectedVisualUpdateStyle = css({
    borderRadius: "8px",
    border: `2px solid ${token("color.link")}`
});

const textContainerStyle = css({
    fontFamily: wacToken("font.charlie.text"),
    padding: token("space.200"),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    textAlign: "start",
    [media.above.md]: {
        padding: `${token("space.300")} ${token("space.300")}`,
        justifyContent: "flex-start",
        flexGrow: 1
    }
});

const imgStyle = css({
    display: "none",
    [media.above.md]: {
        display: "block",
        width: "100%",
        height: "100%",
        flexBasis: "0"
    }
});

const mobileImgStyle = css({
    width: "80px",
    height: "100px",
    alignSelf: "center",
    [media.above.md]: {
        display: "none"
    }
});

const desktopImageContainer = css({
    display: "none",
    [media.above.md]: {
        display: "block",
        marginBlockEnd: token("space.100")
    }
});

const Card: React.FC<CardProps> = ({
    imageSrc,
    mobileImgSrc,
    title,
    subtext,
    setSelectedTemplate,
    templateType,
    templateId,
    setSelectedTemplateId,
    setSelectedCardTitle,
    selectedCardTitle,
    teamType,
    templateMetadata,
    setSelectedTemplateMetadata
}) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const modalVisualUpdates = checkFeatureFlagGate(
        featureFlags.cfind4831ProductOverlayModalVisualUpdates
    );
    return (
        <button
            css={[
                cardBorderStyle,
                modalVisualUpdates
                    ? selectedCardTitle === title
                        ? cardBorderSelectedVisualUpdateStyle
                        : cardBorderVisualUpdateStyle
                    : selectedCardTitle === title
                    ? selectedButtonStyle
                    : unselectedButtonStyle
            ]}
            onClick={() => {
                createAnalyticsEvent({
                    eventType: "ui",
                    action: "clicked",
                    actionSubject: "button",
                    actionSubjectId:
                        TRACKING_EVENT_ACTION_SUBJECT_IDS.templateChoiceCard,
                    attributes: {
                        atlasTicket: "ATLAS-61649",
                        teamType,
                        templateType,
                        templateId,
                        templateName: title
                    }
                }).fire();

                setSelectedTemplate(templateType);
                setSelectedTemplateId(templateId);
                setSelectedCardTitle(title);
                setSelectedTemplateMetadata(templateMetadata);
            }}
        >
            <div css={desktopImageContainer}>
                {/* eslint-disable-next-line no-restricted-syntax */}
                <img src={imageSrc} alt="" css={imgStyle} />
            </div>
            {/* eslint-disable-next-line no-restricted-syntax */}
            <img src={mobileImgSrc} alt="" css={mobileImgStyle} />
            <div
                css={[
                    textContainerStyle,
                    modalVisualUpdates && textContainerVisualUpdateStyle,
                    modalVisualUpdates && cardDescriptionColorOverride
                ]}
            >
                <div
                    css={[
                        titleContainerStyle,
                        modalVisualUpdates && titleContainerVisualUpdateStyle
                    ]}
                >
                    <h3
                        css={[
                            cardTitleStyle,
                            modalVisualUpdates && cardTitleVisualUpdateStyle
                        ]}
                    >
                        {title}
                    </h3>
                </div>
                {modalVisualUpdates ? (
                    <p css={cardDescriptionVisualUpdateStyle}>{subtext}</p>
                ) : (
                    <Text variant="body">{subtext}</Text>
                )}
            </div>
        </button>
    );
};

export default Card;
